import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHeading from '@/components/brand/SectionHeading';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BrainCircuit,
  Monitor,
  Smartphone,
  BarChart3,
  ShoppingBag,
  ExternalLink,
} from 'lucide-react';
import TiltCard from '@/components/brand/TiltCard';
import { PrimaryButton } from '@/components/brand/Buttons';
import { fadeUp, viewportOnce } from '@/lib/motion';
import { HOME } from '@/constants/testIds';

const CATEGORY_ICON_MAP = { BrainCircuit, Monitor, Smartphone, BarChart3, ShoppingBag };

export default function Portfolio({ data, items, categories: liveCategories }) {
  const eyebrow = data?.eyebrow;
  const heading = data?.heading;
  const subhead = data?.subhead;
  const viewAllLabel = data?.view_all_label;

  const categories = (liveCategories || []).map((c) => ({
    id: c.category_id || c.id,
    label: c.label,
    Icon: CATEGORY_ICON_MAP[c.icon] || BrainCircuit,
  }));

  const firstId = categories[0]?.id || 'ai';
  const [active, setActive] = useState(firstId);

  if (!items?.length && !liveCategories?.length) return null;

  const projects = (() => {
    if (Array.isArray(items) && items.length) {
      const grouped = {};
      items.forEach((p) => {
        const key = p.category || 'ai';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push({ title: p.title, tag: p.tag, img: p.img });
      });
      return grouped;
    }
    return {};
  })();

  return (
    <section id="portfolio" className="py-16 bg-white">
      <div className="container mx-auto">

        <SectionHeading
          title={heading}
          eyebrow={eyebrow}
          subtitle={subhead}
          align="center"
          className="mb-10"
        />

        {/* ── Tab bar ── */}
        <div
          data-testid={HOME.portfolioTabs}
          className="relative border-b border-hairline mb-8 overflow-x-auto"
        >
          <div className="flex items-center gap-1 sm:gap-2 min-w-max mx-auto justify-center">
            {categories.map((c) => {
              const Icon = c.Icon;
              const isActive = active === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(c.id)}
                  className={`relative px-4 sm:px-5 py-3 inline-flex items-center gap-2 text-sm font-semibold transition-colors ${
                    isActive ? 'text-navy-deep' : 'text-slatebody hover:text-navy'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${isActive ? 'text-[#C9962E]' : 'text-current'}`} />
                  {c.label}
                  {isActive && (
                    <motion.span
                      layoutId="portfolio-underline"
                      className="absolute left-2 right-2 -bottom-px h-[3px] rounded-full bg-gold-gradient"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Project grid ── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, filter: 'blur(8px)', y: 12 }}
            animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
            exit={{ opacity: 0, filter: 'blur(8px)', y: -8 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {projects[active]?.map((p) => (
              <Link
                key={p.title}
                to="/portfolio"
                aria-label={`View project — ${p.title}`}
                className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E]/60 rounded-2xl"
              >
                {/* Outer motion wrapper for variant propagation */}
                <motion.div
                  whileHover="hovered"
                  initial="rest"
                  animate="rest"
                  className="h-full"
                >
                  <TiltCard className="group rounded-2xl overflow-hidden bg-white border border-hairline shadow-card hover:shadow-soft transition-shadow h-full">

                    {/* Image area */}
                    <div className="relative h-56 overflow-hidden">
                      {/* Image zooms on hover */}
                      <motion.img
                        src={p.img}
                        alt={p.title}
                        className="w-full h-full object-cover"
                        variants={{
                          rest:    { scale: 1 },
                          hovered: { scale: 1.1 },
                        }}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                      />

                      {/* Always-on subtle gradient at bottom */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B35]/40 via-transparent to-transparent" />

                      {/* Tag pill */}
                      <span className="absolute top-3 left-3 text-[10px] font-semibold tracking-wide bg-white/90 backdrop-blur-sm text-navy px-2.5 py-1 rounded-full z-10">
                        {p.tag}
                      </span>

                      {/* Hover overlay — slides up */}
                      <motion.div
                        className="absolute inset-0 flex flex-col justify-end p-5 bg-gradient-to-t from-[#0B1B35] via-[#0B1B35]/85 to-[#0B1B35]/10"
                        variants={{
                          rest:    { opacity: 0 },
                          hovered: { opacity: 1 },
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <motion.div
                          variants={{
                            rest:    { y: 20, opacity: 0 },
                            hovered: { y: 0,  opacity: 1 },
                          }}
                          transition={{ duration: 0.32, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
                        >
                          <p className="text-white font-bold text-base leading-snug mb-2">
                            {p.title}
                          </p>
                          <span className="inline-flex items-center gap-1.5 text-[#E8C25A] text-xs font-semibold">
                            View Project
                            <motion.span
                              variants={{
                                rest:    { x: 0 },
                                hovered: { x: 4 },
                              }}
                              transition={{ duration: 0.25 }}
                            >
                              <ExternalLink className="h-3 w-3" />
                            </motion.span>
                          </span>
                        </motion.div>
                      </motion.div>
                    </div>

                    {/* Card footer */}
                    <div className="px-5 py-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-navy truncate">{p.title}</h4>
                        <span className="text-[11px] text-slatebody">{p.tag}</span>
                      </div>

                      {/* Gold circle arrow — fills on hover */}
                      <motion.div
                        className="shrink-0 h-9 w-9 rounded-full border-2 inline-flex items-center justify-center"
                        variants={{
                          rest:    { backgroundColor: 'rgba(248,250,252,1)', borderColor: 'rgba(226,232,240,1)' },
                          hovered: { backgroundColor: '#C9962E',             borderColor: '#C9962E' },
                        }}
                        transition={{ duration: 0.25 }}
                      >
                        <motion.div
                          variants={{
                            rest:    { color: '#1e3a5f', x: 0 },
                            hovered: { color: '#ffffff', x: 2 },
                          }}
                          transition={{ duration: 0.25 }}
                        >
                          <ArrowRight className="h-3.5 w-3.5" />
                        </motion.div>
                      </motion.div>
                    </div>

                  </TiltCard>
                </motion.div>
              </Link>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* ── Single centered CTA — PrimaryButton with full sheen animation ── */}
        <div className="mt-12 flex justify-center">
          <Link to="/portfolio">
            <PrimaryButton icon={<ArrowRight className="h-4 w-4" />}>
              {viewAllLabel}
            </PrimaryButton>
          </Link>
        </div>

      </div>
    </section>
  );
}
